import { SortInput } from '@apeiron/common'
import { PlanetFilterInput, PlanetSortBy } from '@apeiron/planet'
import { MayNull } from '@apeiron/temp'
import { useQueryParams } from '@src/hooks/common/router/useQueryParam'
import { initGraphqlFilter } from '@src/util/apeiron/planetList'
import { useCallback, useMemo } from 'react'

// TODO - should update to use "useFilterQuery"
export const usePlanetSearchInputs = (props?: Props): Hook => {
  const { sortBy } = props?.defaultInput || {}

  const [query, setQuery, isReady] = useQueryParams(true)

  const filterInput = useMemo<PlanetFilterInput>(
    () => initGraphqlFilter(query || {}),
    [query]
  )

  const sortInput = useMemo(
    () =>
      query && query.sortBy
        ? { sortBy: query.sortBy as PlanetSortBy }
        : { sortBy: sortBy || PlanetSortBy.PriceAsc },
    [query, sortBy]
  )

  const setFilterInput = useCallback(
    (filterInput: PlanetFilterInput) => {
      setQuery(filterInput, sortInput)
    },
    [setQuery, sortInput]
  )

  const setSortInput = useCallback(
    (sortInput: SortInput<PlanetSortBy>) => {
      setQuery(filterInput, sortInput)
    },
    [setQuery, filterInput]
  )

  const setFilterAndSortInput = useCallback(
    (filtering: PlanetFilterInput, sorting: SortInput<PlanetSortBy>) => {
      setQuery(filtering, sorting)
    },
    [setQuery]
  )

  const searchParams: URLSearchParams = useMemo(() => {
    const searchParams = new URLSearchParams()

    if (query) {
      Object.entries(query).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach(v => searchParams.append(key, v))
        } else if (value) {
          searchParams.set(key, value)
        }
      })
    }

    return searchParams
  }, [query])

  return {
    filterInput,
    isReady,
    searchParams,
    setFilterAndSortInput,
    setFilterInput,
    setSortInput,
    sortInput
  }
}

type Props = {
  defaultInput: {
    sortBy?: MayNull<PlanetSortBy>
  }
}

type Hook = {
  filterInput: PlanetFilterInput
  isReady: boolean
  searchParams: URLSearchParams
  setFilterInput: (filterInput: PlanetFilterInput) => void
  setSortInput: (sortInput: SortInput<PlanetSortBy>) => void
  setFilterAndSortInput: (
    filtering: PlanetFilterInput,
    sorting: SortInput<PlanetSortBy>
  ) => void
  sortInput: SortInput<PlanetSortBy>
}
